import { z } from 'zod'

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
})

export type LoginFormValues = z.infer<typeof loginSchema>

export const TRIP_COVER_GRADIENTS = [
  'linear-gradient(150deg,#1d3a4a,#0d1f2b)',
  'linear-gradient(150deg,#2a2f3a,#12161d)',
  'linear-gradient(150deg,#3a2a22,#1a1210)',
  'linear-gradient(150deg,#243a30,#101a15)',
] as const

export const TRIP_CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'JPY', 'CAD'] as const

export const newTripSchema = z
  .object({
    title: z.string().min(1),
    destination: z.string().optional(),
    coverUrl: z.string().optional(),
    startDate: z.string().min(1),
    endDate: z.string().min(1),
    currency: z.enum(TRIP_CURRENCIES),
  })
  .refine((data) => data.endDate >= data.startDate, {
    message: 'La date de retour doit être après la date de départ.',
    path: ['endDate'],
  })

export type NewTripFormValues = z.infer<typeof newTripSchema>

export const ACTIVITY_CATEGORIES = ['transport', 'accommodation', 'discovery', 'meal'] as const

// Untouched form fields submit '' (native input/select default), not undefined.
// Postgres rejects '' for typed columns like `time` and Zod's enum doesn't
// include it either, so every optional field here normalizes '' -> undefined.
const optionalText = () =>
  z
    .string()
    .optional()
    .transform((value) => (value === '' ? undefined : value))

// Coordinates are set programmatically (picking a place-search suggestion),
// never typed by hand — hidden fields, same '' -> undefined normalization,
// then to a number.
const optionalCoordinate = () =>
  z
    .string()
    .optional()
    .transform((value) => (value === '' || value === undefined ? undefined : Number(value)))

export const newActivitySchema = z.object({
  title: z.string().min(1),
  category: z
    .union([z.literal(''), z.enum(ACTIVITY_CATEGORIES)])
    .optional()
    .transform((value) => (value === '' ? undefined : value)),
  location: optionalText(),
  latitude: optionalCoordinate(),
  longitude: optionalCoordinate(),
  startTime: optionalText(),
  endTime: optionalText(),
  note: optionalText(),
})

export type NewActivityFormValues = z.output<typeof newActivitySchema>
export type NewActivityFormInput = z.input<typeof newActivitySchema>

export const BUDGET_CATEGORIES = ['transport', 'accommodation', 'food', 'activities'] as const

// Native number <input> submits a string ('' when untouched) — keep the Zod
// input type as string (matching the real DOM value) and coerce to a number
// in the transform, rather than z.coerce.number() (input type `unknown`,
// awkward with RHF's register()).
const optionalAmount = () =>
  z
    .string()
    .optional()
    .transform((value) => {
      const n = Number(value)
      return Number.isFinite(n) ? n : 0
    })

export const newBudgetItemSchema = z.object({
  label: z.string().min(1),
  category: z
    .union([z.literal(''), z.enum(BUDGET_CATEGORIES)])
    .optional()
    .transform((value) => (value === '' ? undefined : value)),
  plannedAmount: optionalAmount(),
  actualAmount: optionalAmount(),
})

export type NewBudgetItemFormValues = z.output<typeof newBudgetItemSchema>
export type NewBudgetItemFormInput = z.input<typeof newBudgetItemSchema>

export const CHECKLIST_CATEGORIES = ['luggage', 'documents', 'health', 'tasks'] as const

export const newChecklistItemSchema = z.object({
  label: z.string().min(1),
  category: z
    .union([z.literal(''), z.enum(CHECKLIST_CATEGORIES)])
    .optional()
    .transform((value) => (value === '' ? undefined : value)),
})

export type NewChecklistItemFormValues = z.output<typeof newChecklistItemSchema>
export type NewChecklistItemFormInput = z.input<typeof newChecklistItemSchema>

export const DOCUMENT_TYPES = ['passport', 'visa', 'insurance', 'other'] as const

export const newDocumentSchema = z.object({
  type: z.enum(DOCUMENT_TYPES),
  fileUrl: z.string().min(1),
  expirationDate: optionalText(),
})

export type NewDocumentFormValues = z.output<typeof newDocumentSchema>
export type NewDocumentFormInput = z.input<typeof newDocumentSchema>

export const INVITATION_ROLES = ['companion', 'viewer'] as const

export const inviteMemberSchema = z.object({
  email: z.string().email(),
  role: z.enum(INVITATION_ROLES),
})

export type InviteMemberFormValues = z.infer<typeof inviteMemberSchema>

export const BOOKING_TYPES = ['flight', 'hotel', 'transport'] as const

export const newBookingSchema = z.object({
  type: z.enum(BOOKING_TYPES),
  reference: optionalText(),
  startAt: optionalText(),
  endAt: optionalText(),
})

export type NewBookingFormValues = z.output<typeof newBookingSchema>
export type NewBookingFormInput = z.input<typeof newBookingSchema>
